import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { fetchUsers, createUser, updateUser, deleteUser } from '../../utils/usersApi';
import { fetchPositions } from '../../utils/positionsApi';
import { showSuccess, showError } from '../../components/CustomAlert';
import { hasPermission, isAdmin } from '../../utils/permissions';

const Container = styled.div`
  padding: 24px 20px;
  background: #f8f9fa;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  color: #2c3e50;
  margin: 0;
  font-size: 1.8rem;
  font-weight: 600;
`;

const AddButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  &:hover {
    background: rgb(33, 41, 90);
    transform: translateY(-1px);
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow-x: auto;
  width: 100%;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: 16px 20px;
  text-align: left;
  font-weight: 600;
  color: #2c3e50;
  border-bottom: 2px solid #e9ecef;
  font-size: 0.95rem;
`;

const Td = styled.td`
  padding: 16px 20px;
  color: #495057;
  font-size: 0.9rem;
  border-bottom: 1px solid #e9ecef;
`;

const Tr = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
`;

const EditButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  gap: 4px;
  &:hover {
    background: rgb(26, 35, 90);
  }
`;

const Badge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  background: ${(p) => (p.active ? '#d4edda' : '#f8d7da')};
  color: ${(p) => (p.active ? '#155724' : '#721c24')};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #2c3e50;
  font-size: 1.4rem;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  &:hover {
    color: #495057;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 18px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
  color: #495057;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  &:focus {
    outline: none;
    border-color: #3498db;
  }
  &:disabled {
    background: #e9ecef;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  font-size: 1rem;
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const ModalActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: flex-end;
  align-items: center;
  margin-top: 24px;
`;

const ModalDeleteButton = styled.button`
  margin-right: auto;
  background: #c0392b;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover:not(:disabled) {
    background: #922b21;
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const SaveButton = styled.button`
  background: rgb(51, 66, 147);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  &:hover:not(:disabled) {
    background: rgb(33, 41, 90);
  }
  &:disabled {
    background: #6c757d;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  background: #6c757d;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  &:hover {
    background: #5a6268;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #6c757d;
`;

const UserManagement = () => {
  const { user } = useSelector((state) => state.auth);
  const canCreateStaff = hasPermission(user, 'staff_create');
  const canUpdateStaff = hasPermission(user, 'staff_update');

  const [users, setUsers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({
    loginID: '',
    password: '',
    firstName: '',
    lastName: '',
    email: '',
    positionId: '',
    status: 'active',
    newPassword: ''
  });

  const loadUsers = async () => {
    try {
      setLoading(true);
      const list = await fetchUsers();
      setUsers(list);
    } catch (err) {
      showError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadPositions = async () => {
    try {
      const list = await fetchPositions();
      setPositions(list);
    } catch (err) {
      console.error('Failed to load positions', err);
    }
  };

  useEffect(() => {
    loadUsers();
    loadPositions();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm({
      loginID: '',
      password: '',
      firstName: '',
      lastName: '',
      email: '',
      positionId: positions.length ? String(positions[0].id) : '',
      status: 'active',
      newPassword: ''
    });
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      loginID: u.login_id,
      password: '',
      firstName: u.first_name || '',
      lastName: u.last_name || '',
      email: u.email || '',
      positionId: u.position_id ? String(u.position_id) : '',
      status: u.status || 'active',
      newPassword: ''
    });
    setShowModal(true);
  };

  const fullName = (u) => [u.first_name, u.last_name].filter(Boolean).join(' ') || u.login_id;

  const canDeleteUser = (u) =>
    isAdmin(user) && u?.role !== 'admin' && u?.id != null && user?.id != null && Number(u.id) !== Number(user.id);

  const confirmDeleteMessage = (u) =>
    `Delete user "${fullName(u)}" (${u.login_id})? This cannot be undone.`;

  const handleDeleteUser = async (u) => {
    if (!canDeleteUser(u)) return;
    if (!window.confirm(confirmDeleteMessage(u))) return;
    try {
      setDeleting(true);
      await deleteUser(u.id);
      showSuccess('User deleted');
      setShowModal(false);
      setEditing(null);
      loadUsers();
    } catch (err) {
      showError(err.response?.data?.message || err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    if (editing) {
      if (!form.firstName?.trim() || !form.lastName?.trim()) {
        showError('First and last name are required');
        return;
      }
      try {
        setSaving(true);
        const isEditingAdmin = editing.role === 'admin';
        await updateUser(editing.id, {
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim() || undefined,
          ...(isEditingAdmin
            ? {}
            : {
                positionId: form.positionId ? parseInt(form.positionId, 10) : null,
                status: form.status
              }),
          password: form.newPassword.trim() || undefined
        });
        showSuccess('User updated');
        setShowModal(false);
        loadUsers();
      } catch (err) {
        showError(err.response?.data?.message || err.message || 'Update failed');
      } finally {
        setSaving(false);
      }
    } else {
      if (!form.loginID?.trim() || !form.password || form.password.length < 6) {
        showError('Login ID and password (min 6 characters) are required');
        return;
      }
      if (!form.firstName?.trim() || !form.lastName?.trim()) {
        showError('First and last name are required');
        return;
      }
      if (!form.email?.trim()) {
        showError('Email is required');
        return;
      }
      if (!form.positionId) {
        showError('Position is required');
        return;
      }
      try {
        setSaving(true);
        await createUser({
          loginID: form.loginID.trim(),
          password: form.password,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          email: form.email.trim(),
          positionId: parseInt(form.positionId, 10)
        });
        showSuccess('User created');
        setShowModal(false);
        loadUsers();
      } catch (err) {
        showError(err.response?.data?.message || err.message || 'Create failed');
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <Container>
      <Header>
        <Title>User Management</Title>
        {canCreateStaff && (
          <AddButton onClick={openAdd}>
            <FaPlus /> Create User
          </AddButton>
        )}
      </Header>

      <TableContainer>
        {loading ? (
          <LoadingContainer>Loading...</LoadingContainer>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th style={{ width: '20%' }}>Login ID</Th>
                <Th style={{ width: '25%' }}>Name</Th>
                <Th style={{ width: '25%' }}>Email</Th>
                <Th style={{ width: '15%' }}>Position</Th>
                <Th style={{ width: '15%' }}>Status</Th>
                <Th style={{ width: '15%' }}>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <Tr key={u.id}>
                  <Td>{u.login_id}</Td>
                  <Td>{fullName(u)}</Td>
                  <Td>{u.email || '—'}</Td>
                  <Td>{u.position_name || (u.role === 'admin' ? 'Admin' : '—')}</Td>
                  <Td>
                    <Badge active={u.status === 'active'}>{u.status || 'active'}</Badge>
                  </Td>
                  <Td>
                    {((u.role !== 'admin' && canUpdateStaff) || (u.role === 'admin' && isAdmin(user))) && (
                      <EditButton onClick={() => openEdit(u)}>
                        <FaEdit /> Edit
                      </EditButton>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
        {!loading && users.length === 0 && (
          <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
            No staff users yet. Create one and assign a position.
          </div>
        )}
      </TableContainer>

      {showModal && (
        <ModalOverlay onClick={() => !saving && !deleting && setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()} as="form" autoComplete="off" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            <ModalHeader>
              <ModalTitle>{editing ? 'Edit User' : 'Create User'}</ModalTitle>
              <CloseBtn type="button" onClick={() => !saving && !deleting && setShowModal(false)}>&times;</CloseBtn>
            </ModalHeader>

            <FormGroup>
              <Label>First name</Label>
              <Input
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                placeholder="First name"
                autoComplete="off"
              />
            </FormGroup>
            <FormGroup>
              <Label>Last name</Label>
              <Input
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                placeholder="Last name"
                autoComplete="off"
              />
            </FormGroup>
            <FormGroup>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="email@example.com"
                autoComplete="off"
              />
            </FormGroup>

            {editing ? (
              <>
                {editing.role !== 'admin' && (
                  <>
                    <FormGroup>
                      <Label>Position</Label>
                      <Select
                        value={form.positionId}
                        onChange={(e) => setForm((f) => ({ ...f, positionId: e.target.value }))}
                      >
                        <option value="">— None —</option>
                        {positions.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </Select>
                    </FormGroup>
                    <FormGroup>
                      <Label>Status</Label>
                      <Select
                        value={form.status}
                        onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </Select>
                    </FormGroup>
                  </>
                )}
                <FormGroup>
                  <Label>New password (optional)</Label>
                  <Input
                    type="password"
                    value={form.newPassword}
                    onChange={(e) => setForm((f) => ({ ...f, newPassword: e.target.value }))}
                    placeholder="Leave blank to keep current"
                    autoComplete="new-password"
                  />
                </FormGroup>
              </>
            ) : (
              <>
                <FormGroup>
                  <Label>Position</Label>
                  <Select
                    value={form.positionId}
                    onChange={(e) => setForm((f) => ({ ...f, positionId: e.target.value }))}
                  >
                    <option value="">Select position</option>
                    {positions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Login ID</Label>
                  <Input
                    value={form.loginID}
                    onChange={(e) => setForm((f) => ({ ...f, loginID: e.target.value }))}
                    placeholder="e.g. jane_smith"
                    disabled={!!editing}
                    autoComplete="off"
                  />
                </FormGroup>
                <FormGroup>
                  <Label>Password (min 6 characters)</Label>
                  <Input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder="••••••••"
                    autoComplete="new-password"
                  />
                </FormGroup>
              </>
            )}

            <ModalActions>
              {editing && canDeleteUser(editing) && (
                <ModalDeleteButton type="button" disabled={saving || deleting} onClick={() => handleDeleteUser(editing)}>
                  {deleting ? 'Deleting...' : <><FaTrash /> Delete user</>}
                </ModalDeleteButton>
              )}
              <CancelButton type="button" onClick={() => setShowModal(false)} disabled={saving || deleting}>
                Cancel
              </CancelButton>
              {(editing
                ? canUpdateStaff || (editing.role === 'admin' && isAdmin(user))
                : canCreateStaff) && (
                <SaveButton type="submit" disabled={saving || deleting}>
                  {saving ? 'Saving...' : editing ? 'Update' : 'Create User'}
                </SaveButton>
              )}
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default UserManagement;
